/**
 * Reading a whole list off a paginated endpoint, without the caller learning to
 * paginate.
 *
 * Every list in the API answers `{ items, total, limit, offset }` and defaults
 * `limit` to 50. The park has ten outlets today and the ticket asks that fifty
 * need no code change — and a screen that asks for one page silently stops
 * being correct at fifty-one, showing a list that looks complete and is not.
 * That is the failure worth designing out: a missing outlet in a setup screen
 * is not visible as a bug, it is visible as an outlet somebody forgot to
 * create, and it gets created twice.
 *
 * Every back-office list screen — outlets, terminals, users, assignments — has
 * the same shape and the same ceiling, so this is written once here rather than
 * six times in six screens with six different off-by-ones.
 *
 * **This is not a substitute for real pagination.** It is the right answer for
 * a set the park has tens of and reads whole; it is the wrong answer for sales
 * or tickets, which are unbounded and must be paged in the interface. If a
 * screen using this ever starts fetching thousands of rows, that screen needs
 * paging, not a bigger `MAX_PAGES`.
 */

/** The envelope every list operation in this API answers with. */
export interface ListPage<T> {
  readonly items: readonly T[];

  /** Rows matching the query, ignoring limit and offset. */
  readonly total: number;
}

/**
 * Rows per request. Well under the API's documented maximum of 200 and well
 * over any plausible number of outlets, so the ordinary case is one round trip
 * and the loop below is the exception rather than the rule.
 */
export const PAGE_SIZE = 100;

/**
 * A ceiling on round trips, so a server answering an inconsistent `total`
 * cannot spin this forever.
 *
 * It is deliberately generous — 100 pages is 10,000 rows, far beyond anything
 * these screens read — because the point is to bound a bug, not to bound the
 * data. Reaching it returns what was collected rather than throwing: a partial
 * list that renders beats a screen that fails, and the two guards below (an
 * empty page, and a page that did not advance) catch the realistic versions of
 * this long before the counter does.
 */
const MAX_PAGES = 100;

/**
 * Read every page of a list, in order, and return the rows.
 *
 * `fetchPage` is a closure over one operation and its query, so this function
 * knows nothing about paths, credentials or response shapes — the caller passes
 * the generated operation to the generated client and this only counts.
 *
 * The `signal` is the caller's and is not touched here; an aborted request
 * rejects out of `fetchPage` and out of this, which is what stops a toggle
 * flipped twice from landing its first answer on top of its second.
 */
export async function collectPages<T>(
  fetchPage: (offset: number, limit: number) => Promise<ListPage<T>>,
): Promise<T[]> {
  const collected: T[] = [];

  for (let request = 0; request < MAX_PAGES; request += 1) {
    const page = await fetchPage(collected.length, PAGE_SIZE);

    // An empty page is the end of the list, whatever `total` claims. Without
    // this, a `total` larger than the rows actually returned — a row retired
    // between two requests is enough — loops until the counter stops it.
    if (page.items.length === 0) return collected;

    collected.push(...page.items);

    if (collected.length >= page.total) return collected;
  }

  return collected;
}
